import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Compass, MessagesSquare, Target } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Shared";

const VALUES = [
  {
    icon: Compass,
    title: "Craft over shortcuts",
    text: "Everything we ship is built to last, load fast and represent your brand properly. No throwaway templates.",
  },
  {
    icon: MessagesSquare,
    title: "Plain-language communication",
    text: "No jargon and no vanishing acts. You always know what is happening with your project, and why.",
  },
  {
    icon: Target,
    title: "Built around your goals",
    text: "Your website and IT setup should fit the way you actually work, not the other way around.",
  },
];

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="about" data-testid="about-section" className="scroll-mt-20 py-24 md:py-32">
      <div className="container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal className="relative order-2 lg:order-1">
          <div
            ref={imgRef}
            className="relative overflow-hidden rounded-2xl border hairline"
          >
            <motion.img
              src="https://images.unsplash.com/photo-1479226414628-56b454fc107e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="City lights at night in South Africa"
              loading="lazy"
              style={{ y, scale: 1.14 }}
              className="h-[380px] w-full object-cover md:h-[480px]"
            />
            <div
              className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/30"
              aria-hidden="true"
            />
          </div>
          <div className="absolute -bottom-6 left-6 rounded-2xl border hairline bg-card px-6 py-5 shadow-2xl shadow-black/40">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cy">
              Based in South Africa
            </p>
            <p className="mt-1.5 font-grotesk text-sm text-txt">
              Working with clients everywhere.
            </p>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <SectionHead
            eyebrow="Who We Are"
            title="A small studio with a simple promise: technology that serves people."
            sub="We work with small and medium businesses, startups, non-profits, personal brands and anyone who needs a website that earns its keep, or a straight answer to a technical problem."
          />
          <div className="mt-12 flex flex-col gap-8">
            {VALUES.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.08} data-testid={`about-value-${i + 1}`}>
                <div className="flex gap-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border hairline bg-card">
                    <Icon className="h-5 w-5 text-cy" strokeWidth={1.7} />
                  </span>
                  <div>
                    <h3 className="font-grotesk text-base font-semibold tracking-tight text-txt">
                      {title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-sub">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
